// @flow
import React, { useMemo, useCallback, useContext } from "react";
import { MultiSelectionContext } from "data/context";
import { useEffectOnce } from "hooks";
import FolderResult from "./FolderResult";
import DocumentResult from "./DocumentResult";
import Mark from "@hs/mark";
import { type SearchResultRowDTO } from "data/types";
import { hasSomeParentTheClass, getClassNames } from "data/utils";
import { isHandheld } from "data/bowser";
import styles from "./RowResult.module.css";

type Props = {
    /** row index number */
    index: number,
    /** optional CSS style to apply */
    style?: Object,
    /** search item row to render */
    row: SearchResultRowDTO,
    /** callback when search row has been rendered (CellMeasurer) */
    onLoad: () => void,
    /** words to highlight */
    terms: Array<string>,
    /** whether current should be marked as active */
    isActive: boolean,
    /** callback when user clicks on a row
     * @param {number} index the row index clicked
     * @param {SearchResultRowDTO} row the row object itself
     * @param {boolean} ctrlKey whether control-key was pressed
     */
    onClick: (index: number, row: SearchResultRowDTO, ctrlKey: boolean) => void,
    /** callback when user clicks on a row More... button
     * @param {number} index the row index clicked
     * @param {SearchResultRowDTO} row the row object itself
     */
    onMoreClick: (index: number, row: SearchResultRowDTO) => void,
    /** callback when user hovers row
     * @param {number} index the row hovered
     */
    onMouseEnter: (index: number, row: SearchResultRowDTO) => void,
    /** whether breadcrumb should allow navigation */
    isBreadcrumbReadonly?: boolean,
};

const RowResult = ({
    index,
    style,
    row,
    onLoad,
    terms,
    isBreadcrumbReadonly,
    isActive,
    isHovered,
    onClick,
    onContextMenu,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onMoreClick,
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
    onMouseUp,
}: Props) => {
    useEffectOnce(() => {
        onLoad();
    }, []);

    /* istanbul ignore next */
    const { multiple, isSelected } = useContext(MultiSelectionContext) || {
        multiple: false,
        isSelected: (index: number) => false,
    };

    const handleClick = useCallback(
        (e: SyntheticMouseEvent<*>) => {
            e.preventDefault();
            // not clicking on a breadcrumb?
            /* istanbul ignore else */
            if (!hasSomeParentTheClass(e.target, styles.bc)) {
                /* istanbul ignore else */
                if (typeof onClick === "function") onClick(e, index, row);
            }
        },
        [onClick, row, index]
    );

    const combinedClassNames = getClassNames(
        styles.row,
        isHovered ? styles.hovered : null,
        multiple
            ? isSelected(index)
                ? styles.active
                : null
            : isActive
            ? styles.active
            : null,
        isHandheld() ? (multiple ? styles.multi : null) : styles.multi
    );

    const Component = useMemo(
        () => (row.isDoc === true ? DocumentResult : FolderResult),
        [row]
    );

    return (
        <Mark
            word={(row.foundTerms || terms).map((term) =>
                term.replace("*", "")
            )}
            exclude={[".a-name"]}
            // Mark search term but ignore attribute captions!
            style={style}
        >
            <Component
                isBreadcrumbReadonly={!!isBreadcrumbReadonly}
                onClick={handleClick}
                onMoreClick={onMoreClick}
                onMouseEnter={onMouseEnter}
                isActive={isActive}
                isHovered={isHovered}
                className={combinedClassNames}
                index={index}
                row={row}
                actionsDisabled={typeof onMoreClick !== "function"}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onMouseDown={onMouseDown}
                onMouseLeave={onMouseLeave}
                onMouseUp={onMouseUp}
                onContextMenu={onContextMenu}
            />
        </Mark>
    );
};

export default RowResult;
